import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { fetchFrequentlyBoughtTogetherProducts } from "~/ProductDetails/components/FrequentlyBoughtTogetherSection/fetchFrequentlyBoughtTogetherProducts";
import type { ProductData } from "~/models/ProductData";
import { addBaseProductDataAction } from "~/redux/products/actions";
import { ProductService } from "~/AddToCart/ProductService";
import { getInitialPriceReference } from "~/AddToCart/redux/addToCart/getInitialPriceReference";
import { Permission, userHasPermission } from "~/utils/permissions";
import type { ProductDescriptor } from "~/feature/ProductDescriptor/v1/types";

type FrequentlyBoughtTogetherProductsState = "loading" | "loaded" | "error";

export interface FrequentlyBoughtTogetherData {
  loadingFBT: boolean;
  showFBTComponent: boolean;
  mainProduct?: ProductDescriptor;
  firstProduct?: ProductDescriptor;
  secondProduct?: ProductDescriptor;
}

type FrequentlyBoughtTogetherProducts = Awaited<ReturnType<typeof fetchFrequentlyBoughtTogetherProducts>>;

export const useFrequentlyBoughtTogetherProducts = (
  productCode: string,
  featureEnabled: boolean,
): FrequentlyBoughtTogetherData => {
  const [state, setState] = useState<FrequentlyBoughtTogetherProductsState>("loading");
  const [products, setProducts] = useState<FrequentlyBoughtTogetherProducts | null>(null);
  const dispatch = useDispatch();

  useEffect(() => {
    (async () => {
      if (!featureEnabled) {
        return;
      }
      const products = await fetchFrequentlyBoughtTogetherProducts(productCode);
      dispatch(addBaseProductDataAction([...products.first, ...products.second]));

      setProducts(products);
      setState("loaded");
    })();
  }, [productCode, featureEnabled]);

  const productCodes = products ? [...products.first, ...products.second].map((product) => product.code) : [];

  const mainProduct = ProductService.useProductDescriptor({
    productCode: productCode,
    priceReference: getInitialPriceReference(),
    options: { fetch: true },
  });

  const productDescriptors = ProductService.useMultipleProductDescriptors({
    productCodes: productCodes,
    priceReference: getInitialPriceReference(),
    options: { fetch: true },
  });

  if (!featureEnabled) {
    return {
      loadingFBT: false,
      showFBTComponent: false,
    };
  }

  if (state === "loading") {
    return {
      loadingFBT: true,
      showFBTComponent: true,
    };
  }

  let firstProduct: ProductDescriptor | undefined = undefined;
  let secondProduct: ProductDescriptor | undefined = undefined;

  function pickProduct(
    firstRelation: ProductData[] | undefined,
    secondRelation: ProductData[] | undefined,
    excludeProduct?: ProductDescriptor,
  ) {
    const productsToCheck = [...(firstRelation ?? []), ...(secondRelation ?? [])]
      .map((product) => productDescriptors[product.code])
      .filter((product) => product !== excludeProduct);
    if (!userHasPermission(Permission.AVAILABILITY)) {
      return productsToCheck[0];
    } else {
      for (const productDescriptor of productsToCheck) {
        if (!excludeProduct || excludeProduct !== productDescriptor) {
          return productDescriptor;
        }
      }
      return undefined;
    }
  }
  firstProduct = pickProduct(products?.first, products?.second);
  if (firstProduct) {
    secondProduct = pickProduct(products?.second, products?.first, firstProduct);
  }

  const allSecondProductCandidates = [...(products?.first ?? []), ...(products?.second ?? [])].filter(
    (product) => product !== firstProduct?.base,
  );

  return {
    // False when there's a second product. Otherwise true if not all loaded
    loadingFBT: !secondProduct && allSecondProductCandidates.length > 0,
    // Show when there's a first product or we're still loading
    showFBTComponent: !!firstProduct,
    mainProduct,
    firstProduct,
    secondProduct,
  };
};
